import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { format } from "@formkit/tempo";
import type { ConversationWindow } from "@/lib/query/conversation/conversation.types";

interface ConversationWindowClosedIndicatorProps {
  window: ConversationWindow;
}

export function ConversationWindowClosedIndicator({
  window,
}: ConversationWindowClosedIndicatorProps) {
  const { t } = useTranslation();

  const formattedTime = useMemo(() => {
    try {
      const dateStr = window.closedAt || window.openedAt;
      return format(new Date(dateStr), "HH:mm");
    } catch {
      return "";
    }
  }, [window.closedAt, window.openedAt]);

  const label = useMemo(() => {
    if (window.closedAt) {
      return t("common:misc.windowClosed", "Venster gesloten");
    }
    return t("common:misc.windowExpired", "Venster verlopen");
  }, [window.closedAt, t]);

  return (
    <div className="relative my-3 flex items-center">
      {/* Left horizontal line */}
      <div className="h-px flex-1 bg-bordercolor-primary" />

      {/* Center indicator pill */}
      <div className="mx-2 flex items-center gap-1.5 rounded-lg border border-bordercolor-primary bg-gray-50 px-3 py-1.5">
        <span className="text-xs font-medium text-gray-600">
          {label}: {formattedTime}
        </span>
        <span className="size-2 rounded-full bg-gray-500" />
      </div>

      {/* Right horizontal line */}
      <div className="h-px flex-1 bg-bordercolor-primary" />
    </div>
  );
}
